<template>
  <div
    class="tw:flex tw:items-stretch tw:gap-5 tw:rounded-md tw:border tw:border-solid tw:border-border tw:bg-white tw:px-4 tw:shadow-xs tw:transition-shadow tw:hover:shadow-raised">
    <!-- initials tile — 44x44 per spec (not a DS Avatar size; kept as inline element) -->
    <span
      class="tw:mt-4 tw:flex tw:size-11 tw:shrink-0 tw:grow-0 tw:items-center tw:justify-center tw:self-start tw:overflow-hidden tw:rounded-md tw:border tw:border-solid tw:border-border-secondary tw:bg-gray-50 tw:text-13 tw:font-semibold tw:text-gray-500">
      <img
        v-if="logoUrl"
        :src="logoUrl"
        :alt="`${props.order.company_name ?? ''} logo`"
        class="tw:size-full tw:object-contain" />
      <template v-else>{{ initials }}</template>
    </span>

    <!-- title block: company · id, order title, pill row -->
    <div class="tw:flex tw:min-w-0 tw:flex-1 tw:flex-col tw:py-4">
      <div class="tw:flex tw:min-w-0 tw:items-center tw:gap-2 tw:text-13 tw:font-normal tw:text-muted">
        <RouterLink
          v-if="props.order.company_name"
          :to="{ name: 'orders.list', params: { company_name: props.order.company_name } }"
          class="tw:truncate tw:text-muted! tw:no-underline tw:hover:text-gray-700! tw:hover:underline">
          {{ props.order.company_name }}
        </RouterLink>
        <span v-if="props.order.provider_job_id" class="tw:text-gray-300">·</span>
        <span v-if="props.order.provider_job_id" class="tw:shrink-0 tw:tabular-nums">
          {{ props.order.provider_job_id }}
        </span>
      </div>
      <RouterLink
        :to="getOrderRoute(props.order, selfServiceEnabled)"
        :title="props.order.title"
        data-testid="orders-list-title"
        class="tw:mt-0.5 tw:block tw:truncate tw:text-16 tw:font-semibold tw:text-gray-900! tw:no-underline tw:hover:text-gray-900! tw:hover:underline">
        {{ props.order.title }}
      </RouterLink>
      <OrderPills :order="props.order" class="tw:mt-auto tw:pt-3" />
    </div>

    <!-- metrics panel: Last 7 days · Impressions · Clicks · CTR -->
    <div
      class="tw:hidden tw:shrink-0 tw:border-l tw:border-solid tw:border-border-secondary tw:pl-5 tw:xl:block"
      style="width: 460px">
      <div class="tw:grid tw:gap-6 tw:py-4" style="grid-template-columns: 120px 1fr 1fr; align-content: start">
        <!-- Last 7 days: sparkline of clicks -->
        <div class="tw:flex tw:flex-col">
          <span class="tw:text-11 tw:font-semibold tw:tracking-wider tw:uppercase tw:text-gray-400">
            {{ $t('orders.grid.last_7_days') }}
          </span>
          <span class="tw:mt-2 tw:flex tw:items-end tw:gap-1.5" style="height: 26px">
            <span
              v-for="(bar, i) in sparkBars"
              :key="i"
              class="tw:block tw:rounded-[2px] tw:bg-primary"
              :style="{ width: '8px', height: bar.h, opacity: bar.opacity }" />
          </span>
        </div>
        <!-- Impressions -->
        <div class="tw:flex tw:flex-col">
          <span class="tw:text-11 tw:font-semibold tw:tracking-wider tw:uppercase tw:text-gray-400">
            {{ $t('orders.list.impressions') }}
          </span>
          <span class="tw:mt-1.5 tw:text-18 tw:font-semibold tw:tabular-nums tw:text-gray-900">
            {{ formatCountOrDash(props.order.stats_impressions_total) }}
          </span>
          <span class="tw:mt-2 tw:text-12 tw:font-normal tw:text-muted">
            {{ runtimeText(props.order) }}
          </span>
        </div>
        <!-- Clicks · CTR -->
        <div class="tw:flex tw:flex-col">
          <span class="tw:text-11 tw:font-semibold tw:tracking-wider tw:uppercase tw:text-gray-400">
            {{ $t('orders.grid.clicks_ctr') }}
          </span>
          <span class="tw:mt-1.5 tw:text-18 tw:font-semibold tw:tabular-nums tw:text-gray-900">
            {{ formatCountOrDash(props.order.stats_clicks_total) }}
          </span>
          <span class="tw:mt-2 tw:text-12 tw:font-normal tw:tabular-nums tw:text-muted">
            {{ ctrText(props.order) }}
          </span>
        </div>
      </div>
    </div>

    <!-- creative preview (reuses existing OrderBanner which fetches the ad's preview image) -->
    <div class="tw:flex tw:shrink-0 tw:items-center tw:self-center">
      <OrderBanner :order="props.order" />
    </div>

    <!-- trailing actions -->
    <div class="tw:flex tw:shrink-0 tw:items-center">
      <OrderActionsMenu :order="props.order" @delete="emit('delete', $event)" />
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue';
import { RouterLink } from 'vue-router';
import type { Order } from '@/types/types';
import OrderActionsMenu from './OrderActionsMenu.vue';
import { extractStats } from '@/func/statsCache';
import {
  companyLogoUrl,
  companyInitials,
  runtimeText,
  ctrText,
  formatCountOrDash,
} from '@/components/orders/orderFormatters';
import OrderBanner from './order_elements/OrderBanner.vue';
import OrderPills from './order_elements/OrderPills.vue';
import { getOrderRoute } from '@/func/orderCreation';
import { useCustomerFeaturesStore } from '@/stores/customerFeatures';

const featureStore = useCustomerFeaturesStore();
const selfServiceEnabled = computed(() => featureStore.featureOrderManagment?.self_service === true);

const props = defineProps<{ order: Order }>();
const emit = defineEmits<{ (e: 'delete', order: Order): void }>();

/* --------------------------------- COMPUTED ------------------------------- */

const logoUrl = computed(() => companyLogoUrl(props.order));
const initials = computed(() => companyInitials(props.order));

// 7 vertical bars sized by daily clicks. Height is proportional to the day's share of the 7-day max
// so orders with lower absolute traffic still get a readable shape. Empty days fall back to a slim
// baseline mark instead of vanishing entirely.
const sparkBars = computed(() => {
  const items = extractStats(props.order.stats_cache, 'clicks');
  const max = Math.max(1, ...items.map((it) => it.count));
  return items.map((it) => {
    const ratio = it.count / max;
    const h = it.count === 0 ? '2px' : `${Math.max(4, Math.round(ratio * 26))}px`;
    // fade empty days so the chart reads as "no traffic yet" without an empty column gap.
    const opacity = it.count === 0 ? 0.25 : 1;
    return { h, opacity };
  });
});
</script>
